import ResponsiveImage from "../responsive-image/ResponsiveImage";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./Shop.css";

// Function to load shop data from public folder
const loadShopData = async () => {
  try {
    const response = await fetch("/shopData.json");
    if (!response.ok) {
      throw new Error("Failed to load shop data");
    }
    return await response.json();
  } catch (error) {
    console.error("Error loading shop data:", error);
    return {
      shopItems: [],
      shopTitle: {},
      shopSubtitle: {},
      moreButton: {},
      ariaLabels: {},
    };
  }
};

// Function to dynamically import shop images
const importShopImages = async (shopItems) => {
  const imageModules = {};

  for (const item of shopItems) {
    try {
      // Try to import from editableSources first
      let imageModule;
      try {
        imageModule = await import(
          `../../editableSources/shopImages/${item.imageName}`
        );
      } catch {
        // Fallback to placeholder if image doesn't exist
        console.warn(
          `Could not load shop image: ${item.imageName}, using placeholder`
        );
        continue;
      }
      imageModules[item.imageName] = imageModule.default;
    } catch (error) {
      console.warn(`Could not load shop image: ${item.imageName}`, error);
    }
  }

  return imageModules;
};

const Shop = () => {
  const { i18n } = useTranslation();
  const [shopItems, setShopItems] = useState([]);
  const [shopData, setShopData] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Helper function to get text in current language
  const getLocalizedText = (textObj, fallback = "") => {
    if (!textObj) return fallback;
    const currentLang = i18n.language || "de";
    return textObj[currentLang] || textObj["de"] || textObj["en"] || fallback;
  };

  // Load shop data and images on component mount
  useEffect(() => {
    const loadShopContent = async () => {
      const loadedShopData = await loadShopData();
      const imageModules = await importShopImages(loadedShopData.shopItems);

      // Create placeholder images for items without actual images
      const itemsWithImages = loadedShopData.shopItems.map((item) => ({
        ...item,
        image:
          imageModules[item.imageName] ||
          `https://via.placeholder.com/400x600/8B4513/FFFFFF?text=${encodeURIComponent(
            getLocalizedText(item.title)
          )}`,
      }));

      setShopData(loadedShopData);
      setShopItems(itemsWithImages);
      setImagesLoaded(true);
    };

    loadShopContent();
  }, [i18n.language]);

  const handleMoreClick = () => {
    // Navigate to buy&rent page
    window.location.href =
      i18n.language === "en" ? "/en/buy&rent" : "/buy&rent";
  };

  const handleTileClick = (item) => {
    // Navigate to detailed product page
    window.location.href =
      i18n.language === "en" ? `/en/product/${item.id}` : `/product/${item.id}`;
  };

  // Keyboard handlers
  const handleMoreKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleMoreClick();
    }
  };

  const handleTileKeyDown = (e, item) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleTileClick(item);
    }
  };

  return (
    <div className="shop-section">
      <div className="shop-header">
        <h2 className="shop-title">
          {shopData
            ? getLocalizedText(shopData.shopTitle, "Unser Shop")
            : "Unser Shop"}
        </h2>
        <p className="shop-subtitle">
          {shopData
            ? getLocalizedText(
                shopData.shopSubtitle,
                "Handgefertigte Meisterwerke"
              )
            : "Handgefertigte Meisterwerke"}
        </p>
      </div>

      {!imagesLoaded ? (
        <div className="shop-loading">
          <p>Loading shop...</p>
        </div>
      ) : (
        <>
          <div className="shop-grid">
            {shopItems.map((item) => (
              <div
                key={item.id}
                className="shop-tile focus-interactive"
                onClick={() => handleTileClick(item)}
                onKeyDown={(e) => handleTileKeyDown(e, item)}
                tabIndex={0}
                role="button"
                aria-label={`${getLocalizedText(
                  item.title
                )} - ${getLocalizedText(item.description)} - ${
                  shopData?.ariaLabels?.shopTile
                    ? getLocalizedText(shopData.ariaLabels.shopTile)
                    : "Zum Shop-Bereich navigieren"
                }`}
              >
                <div className="shop-tile-image-container">
                  <ResponsiveImage sizes="(max-width: 768px) 96vw, 30vw"
                    src={item.image}
                    alt={getLocalizedText(
                      item.altText,
                      getLocalizedText(item.title)
                    )}
                    className="shop-tile-image"
                  />
                </div>
                <div className="shop-tile-content">
                  <h3 className="shop-tile-title">
                    {getLocalizedText(item.title)}
                  </h3>
                  <p className="shop-tile-description">
                    {getLocalizedText(item.description)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="shop-more-section">
            <button
              className="shop-more-button focus-button"
              onClick={handleMoreClick}
              onKeyDown={handleMoreKeyDown}
              tabIndex={0}
              aria-label={
                shopData?.ariaLabels?.moreButton
                  ? getLocalizedText(shopData.ariaLabels.moreButton)
                  : "Zum vollständigen Shop navigieren"
              }
            >
              {shopData
                ? getLocalizedText(shopData.moreButton, "Mehr anzeigen")
                : "Mehr anzeigen"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Shop;
